import { StorageAdapter } from '../storage/StorageAdapter'
import { rowsOf } from '../storage/rows'
import { dbgWarn } from '../logger'
import { getOfflineUserId } from '../session'

/**
 * photos/photoIssues.js — DETECCIÓN VISIBLE de fotos que NO llegaron al servidor (anti pérdida-silenciosa).
 *
 * Cierra los huecos de la auditoría de pérdida de fotos: convierte casos antes silenciosos en una fila
 * durable de `sync_notifications` que la UI (usePhotoIssues -> TheSyncDrawer/TheSyncBanner) muestra para
 * que el operador RE-TOME la foto. Tres `kind`:
 *   - photo_missing        : binario evictado/ilegible ANTES de subir (lo escribe modules/occurrences.js).
 *   - photo_capture_failed : la captura/escritura del binario falló ANTES de persistir fila+outbox (A).
 *   - photo_orphan         : photo_meta pending sin op de outbox = la fila de datos nunca se escribió
 *                            (crash entre capture() y saveAnswer/saveAddon, o write de raíz fallido) (F).
 *
 * SQL SOLO via el `db` inyectable (default = StorageAdapter, STOR-05). Escribir via StorageAdapter dispara
 * el emit post-commit de 'sync_notifications' -> usePhotoIssues se refresca solo.
 */

const PHOTO_ISSUE_KINDS = ['photo_missing', 'photo_capture_failed', 'photo_orphan']

/** Inserta una nota idempotente por (client_uuid, kind): no re-inserta si ya hay una abierta. */
async function _insertOnce ({ entity, clientUuid, kind, db, now }) {
  const existing = await db.query(
    'SELECT id FROM sync_notifications WHERE client_uuid=? AND kind=? AND dismissed=0 LIMIT 1',
    [String(clientUuid), kind]
  )
  if (existing && existing.values && existing.values.length) return false
  await db.run(
    'INSERT INTO sync_notifications (entity, client_uuid, kind, dismissed, created_at, user_id) VALUES (?,?,?,0,?,?)',
    [entity, String(clientUuid), kind, now != null ? now : Date.now(), getOfflineUserId()],
    true
  )
  return true
}

/**
 * A — la captura/escritura del binario falló ANTES de persistir fila/outbox (cuota web / FS lleno):
 * deja rastro DURABLE para que la UI avise "re-toma la foto" (antes solo iba a consola = silencioso).
 * Idempotente por (occurrence,item). Best-effort: nunca lanza (no debe romper el flujo del llamador).
 */
export async function reportPhotoCaptureFailed ({ occurrenceId, itemId, db = StorageAdapter } = {}) {
  try {
    const key = `${occurrenceId ?? '?'}:${itemId ?? '?'}`
    await _insertOnce({ entity: 'occurrence', clientUuid: key, kind: 'photo_capture_failed', db })
    dbgWarn('photo', `captura de foto FALLÓ (occ=${occurrenceId} item=${itemId}) -> registrada para re-tomar`)
  } catch (e) {
    dbgWarn('photo', 'reportPhotoCaptureFailed falló (ignorado)', e)
  }
}

/**
 * F — sweep de huérfanas: photo_meta pending cuyo parent_uuid no tiene NINGÚN op de outbox = la fila de
 * datos/op nunca se escribió. Solo cuenta las más viejas que graceMs (evita falso-positivo de una
 * captura recién en curso cuyo saveAnswer aún no commitea). Idempotente por parent_uuid. Best-effort.
 * @returns {Promise<{ reported: number }>}
 */
export async function sweepOrphanPhotos ({ db = StorageAdapter, now = Date.now(), graceMs = 60000 } = {}) {
  // photo_meta.created_at se guarda como ISO string (PhotoStore.capture) -> cutoff también ISO (compara lexicográfico).
  const cutoffIso = new Date(now - graceMs).toISOString()
  // FIX (review Hugo): el NOT EXISTS debe contar SOLO ops que puedan pertenecer a ESTA foto — es decir,
  // creados EN/DESPUÉS de su captura. Sin esto, un 'done' VIEJO de una foto A (mismo parent_uuid
  // determinista ans:<occ>:<item>) satisfacía el EXISTS y ocultaba que B (re-toma, sin op) es huérfana:
  // "done viejo + foto sin op" ahora SÍ dispara photo_orphan. Formatos: pm ISO -> epoch ms con julianday.
  const res = await db.query(
    'SELECT image_ref, parent_uuid, item_id FROM photo_meta pm ' +
    "WHERE pm.upload_status='pending' AND pm.parent_uuid IS NOT NULL AND pm.created_at < ? " +
    'AND NOT EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = pm.parent_uuid ' +
    'AND o.created_at >= ((julianday(pm.created_at) - 2440587.5) * 86400000.0))',
    [cutoffIso]
  )
  const rows = rowsOf(res)
  let reported = 0
  for (const row of rows) {
    try {
      if (await _insertOnce({ entity: 'occurrence', clientUuid: row.parent_uuid, kind: 'photo_orphan', db, now })) reported++
    } catch (e) {
      dbgWarn('photo', 'sweepOrphanPhotos: fila saltada', e)
    }
  }
  if (reported) dbgWarn('photo', `sweepOrphanPhotos: ${reported} foto(s) huérfana(s) reportada(s)`)
  return { reported }
}

/**
 * Lista las incidencias de foto ABIERTAS del USUARIO ACTUAL (device compartido, review Hugo):
 * B no ve las fotos-perdidas de A. Filas legacy NULL degradan a visibles para cualquiera (igual que
 * el drain). `getOfflineUserId()` null → sin filtro. Más recientes primero.
 */
export async function listPhotoIssues ({ db = StorageAdapter } = {}) {
  const placeholders = PHOTO_ISSUE_KINDS.map(() => '?').join(',')
  const uid = getOfflineUserId()
  const scope = uid == null ? '' : ' AND (user_id IS NULL OR user_id=?)'
  const params = uid == null ? [...PHOTO_ISSUE_KINDS] : [...PHOTO_ISSUE_KINDS, uid]
  const res = await db.query(
    'SELECT id, entity, client_uuid, kind, created_at FROM sync_notifications ' +
    `WHERE kind IN (${placeholders}) AND dismissed=0${scope} ORDER BY created_at DESC`,
    params
  )
  return (res && res.values) || []
}

/** Marca una incidencia como vista/descartada (el operador ya re-tomó o ya la atendió). */
export async function dismissPhotoIssue (id, { db = StorageAdapter } = {}) {
  if (id == null) return
  await db.run('UPDATE sync_notifications SET dismissed=1 WHERE id=?', [id], true)
}
